import type { AddressForm, WarehouseOption } from './types';

/**
 * Required text fields, in the order they are reported. The form surfaces one
 * message at a time, so the order is what the user walks down the form.
 */
const REQUIRED_FIELDS: Array<[keyof AddressForm, string]> = [
  ['fullName', 'Full name is required'],
  ['phone', 'Phone is required'],
  ['line1', 'Address line 1 is required'],
  ['city', 'City is required'],
  ['state', 'State is required'],
  ['postalCode', 'Postal code is required'],
  ['country', 'Country is required'],
];

/**
 * Returns the first problem, or null when the form is saveable.
 *
 * The fulfilment-method rules mirror the server's, so the same mistake is
 * caught before the request rather than coming back as LOAD_OPTION_REQUIRED /
 * DEFAULT_LOAD_OPTION_NOT_ASSIGNED.
 */
export const validateAddressForm = (
  form: AddressForm,
  warehouses: WarehouseOption[]
): string | null => {
  for (const [field, message] of REQUIRED_FIELDS) {
    if (!String(form[field] ?? '').trim()) {
      return message;
    }
  }

  const selectedMethodIds = Array.isArray(form.orgLoadOptionIds) ? form.orgLoadOptionIds : [];

  // No methods selected is valid: the location inherits the org-wide default. A
  // default is only required (and checked) when the location has its own methods.
  if (selectedMethodIds.length > 0) {
    if (!form.defaultFulfillmentMethodId) {
      return 'Select a default fulfilment method';
    }

    if (!selectedMethodIds.includes(form.defaultFulfillmentMethodId)) {
      return 'The default fulfilment method must be assigned to this location';
    }
  }

  for (const [warehouseId, rawValue] of Object.entries(form.carrierRates ?? {})) {
    const normalizedValue = String(rawValue ?? '').trim();

    // A blank rate means "no rate for this warehouse", which is allowed.
    if (!normalizedValue) {
      continue;
    }

    const warehouseName = () =>
      (warehouses || []).find((item) => item.id === warehouseId)?.name || 'warehouse';

    const carrierRate = Number(normalizedValue);

    if (!Number.isFinite(carrierRate) || carrierRate < 0) {
      return `Carrier rate for ${warehouseName()} must be a valid non-negative amount`;
    }

    const decimalPlaces = normalizedValue.split('.')[1]?.length ?? 0;

    if (decimalPlaces > 2) {
      return `Carrier rate for ${warehouseName()} cannot have more than two decimal places`;
    }
  }

  return null;
};
